import { useEffect, useMemo } from 'react';
import { PRODUCT_AVAILABILITY } from '../../logic/productAvailability';

// Split out of useTripDayHotelSelection (US-061) to stay under the package's
// max-lines-per-function cap. KD-3643 §2 — a stay whose rooms are gone, or a
// hotel the DMC never distributed to us, is no more bookable than an
// unavailable program; reports that verdict up through `registerHotelWarning`
// (TO-only today, a no-op prop for any caller that doesn't pass one).
export function useHotelAvailabilityVerdict({
  dayIndex,
  hotel,
  hotelIsNotDistributed,
  hotelGroup,
  isFirstInGroup,
  isIncludedHotel,
  isGroupLoading,
  hasGroupError,
  isRoomUnavailable,
  isMinStayUnmet,
  hasBookablePrice,
  roomStartDate,
  roomEndDate,
  roomsData,
  roomsError,
  registerHotelWarning,
}: {
  dayIndex: number;
  hotel: any;
  hotelIsNotDistributed: boolean;
  hotelGroup: any;
  isFirstInGroup: boolean;
  isIncludedHotel: any;
  isGroupLoading: boolean;
  hasGroupError: boolean;
  isRoomUnavailable: any;
  isMinStayUnmet: boolean;
  hasBookablePrice: boolean;
  roomStartDate: any;
  roomEndDate: any;
  roomsData: any;
  roomsError: any;
  registerHotelWarning?: (key: any, status: any) => void;
}) {
  // Being undistributed is decided day by day (`day.hotelIsAccessible`), so
  // it is reported under a key of its own rather than the stay's: a day in
  // the middle of a group has a verdict to give here, and must not have to
  // borrow — or overwrite — the key the first day speaks through.
  const isUndistributedDayVerdict = !!hotel?.id && hotelIsNotDistributed;
  const hotelAvailabilityKey = isUndistributedDayVerdict
    ? `day-${dayIndex}`
    : (hotelGroup?.startDayIndex ?? dayIndex);
  const ownsHotelAvailability =
    isUndistributedDayVerdict || !hotelGroup || isFirstInGroup;

  const hotelAvailabilityStatus = useMemo(() => {
    if (!hotel?.id) {
      return null;
    }

    if (hotelIsNotDistributed) {
      return PRODUCT_AVAILABILITY.UNAVAILABLE;
    }

    // Everything below is the room availability request talking, and it only
    // runs for a non-included hotel on the first day of a group.
    if (!isFirstInGroup || isIncludedHotel) {
      return null;
    }

    const isRoomCheckRunning = !!roomStartDate && !!roomEndDate;

    if (
      isGroupLoading ||
      (isRoomCheckRunning && roomsData === undefined && !roomsError)
    ) {
      return PRODUCT_AVAILABILITY.PENDING;
    }

    return hasGroupError ||
      isRoomUnavailable ||
      isMinStayUnmet ||
      !hasBookablePrice
      ? PRODUCT_AVAILABILITY.UNAVAILABLE
      : null;
  }, [
    hotel?.id,
    hotelIsNotDistributed,
    isFirstInGroup,
    isIncludedHotel,
    isGroupLoading,
    hasGroupError,
    isRoomUnavailable,
    isMinStayUnmet,
    hasBookablePrice,
    roomStartDate,
    roomEndDate,
    roomsData,
    roomsError,
  ]);

  useEffect(() => {
    if (!registerHotelWarning || !ownsHotelAvailability) {
      return undefined;
    }

    const key = hotelAvailabilityKey;
    registerHotelWarning(key, hotelAvailabilityStatus);

    return () => registerHotelWarning(key, null);
  }, [
    registerHotelWarning,
    ownsHotelAvailability,
    hotelAvailabilityKey,
    hotelAvailabilityStatus,
  ]);
}
